import { useEffect, useState } from "react";
import { useIntl } from "react-intl";
import { Button } from "primereact/button";
import { Divider } from "primereact/divider";
import {
  clearSignedOut,
  isPasswordSignInOff,
  isSignedOut,
  redirectTo,
  ssoStartUrl,
} from "../../../helpers/SsoHelper";
import PreLoginContainer from "../../common/preLoginContainer";

// Starts a company sign-in. A later visit to the sign-in pages (such as after
// an expired session) may go straight to the company's sign-in page again.
const startCompanySignIn = (subDomain) => {
  clearSignedOut();
  redirectTo(ssoStartUrl(subDomain));
};

// While password sign-in is off the sign-in pages go straight to the
// company's sign-in page, unless the person has just signed out; returns
// whether the pages show the company sign-in instead of their form.
export const useCompanySignInRedirect = (enterprise) => {
  const passwordOff = isPasswordSignInOff(enterprise);
  const subDomain = enterprise?.subDomain;
  const [signedOut] = useState(isSignedOut);

  useEffect(() => {
    if (passwordOff && subDomain && !signedOut) {
      redirectTo(ssoStartUrl(subDomain));
    }
  }, [passwordOff, subDomain, signedOut]);

  return passwordOff;
};

// Above the username and password form while company sign-in is on: like
// the sign-in buttons (label, then icon) but outlined, so it stands apart.
export const CompanySignInButton = ({ enterprise }) => {
  const intl = useIntl();
  if (!enterprise?.ssoEnabled || !enterprise?.subDomain) {
    return null;
  }

  return (
    <div className="flex flex-column gap-3 mt-4">
      <Button
        type="button"
        className="w-full"
        outlined
        onClick={() => startCompanySignIn(enterprise.subDomain)}
      >
        <div className="gap-2 flex justify-content-center w-full">
          <span>{intl.formatMessage({ id: "sso_sign_in_button" })}</span>
          <i className="pi pi-building"></i>
        </div>
      </Button>
      <Divider align="center" className="my-1">
        <span className="text-600 text-sm">
          {intl.formatMessage({ id: "sso_or" })}
        </span>
      </Divider>
    </div>
  );
};

// The whole sign-in page while it goes to the company's sign-in page, or,
// just after signing out, a button to go there.
export const CompanySignInRedirecting = ({ enterprise }) => {
  const intl = useIntl();
  const [signedOut] = useState(isSignedOut);

  if (signedOut && enterprise?.subDomain) {
    return (
      <PreLoginContainer showLoginMessage={true}>
        <div className="tag-line text-muted text-center mt-4">
          {intl.formatMessage({ id: "sso_signed_out" })}
        </div>
        <Button
          type="button"
          className="w-full mt-4"
          onClick={() => startCompanySignIn(enterprise.subDomain)}
        >
          <div className="gap-2 flex justify-content-center w-full">
            <span>{intl.formatMessage({ id: "sso_sign_in_button" })}</span>
            <i className="pi pi-building"></i>
          </div>
        </Button>
      </PreLoginContainer>
    );
  }

  return (
    <PreLoginContainer showLoginMessage={true}>
      <div className="tag-line text-muted text-center mt-4">
        {intl.formatMessage({ id: "sso_redirecting" })}
      </div>
    </PreLoginContainer>
  );
};
